import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { conversationKeyFor, previewText, splitKey } from '@/utils/commsFormat';
import { playChime, playIncomingChirp } from '@/utils/audioCues';
import { speak } from '@/utils/tts';
import { showMessageNotification, updateNotificationTranscript } from '@/utils/notifications';
import { useCommsSocket } from '@/hooks/useCommsSocket';
import { useAlertSettings } from '@/hooks/useAlertSettings';
import { useVoicePlayer } from '@/hooks/useVoicePlayer';
import { useQuietHours } from '@/hooks/useQuietHours';

const CommsContext = createContext(null);
export const useComms = () => useContext(CommsContext);

const REFRESH_MS = 60000;

// App-wide messaging state: socket, conversation list, unread counts, alerts and radio playback.
export function CommsProvider({ user, children }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const conversationsRef = useRef([]);
  conversationsRef.current = conversations;
  const [loading, setLoading] = useState(true);
  const [onlineIds, setOnlineIds] = useState(() => new Set());
  const settings = useAlertSettings();
  const quiet = useQuietHours();
  const player = useVoicePlayer({ enabled: settings.soundsOn, userId: user.id });
  const isQuietNowRef = useRef(quiet.isQuietNow);
  isQuietNowRef.current = quiet.isQuietNow;

  const activeRef = useRef(null); // conversation key that is open AND visible
  const listenersRef = useRef(new Set());
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const refreshConversations = useCallback(async () => {
    try {
      const data = await commsApi.conversations();
      setConversations(data.conversations);
      setOnlineIds(new Set(data.online_user_ids));
    } catch {
      /* keep the previous list; the badge self-corrects on the next refresh */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshConversations();
    const timer = setInterval(refreshConversations, REFRESH_MS);
    window.addEventListener('focus', refreshConversations);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refreshConversations);
    };
  }, [refreshConversations]);

  const openConversation = useCallback((key) => navigate(`/dashboard/communications?c=${encodeURIComponent(key)}`), [navigate]);

  const markRead = useCallback(async (key) => {
    setConversations((prev) => prev.map((c) => (c.key === key ? { ...c, unread_count: 0 } : c)));
    const [kind, id] = splitKey(key);
    try {
      if (kind === 'team') await commsApi.markChannelRead(id);
      else if (kind === 'user') await commsApi.markRead(id);
    } catch { /* self-corrects on refresh */ }
  }, []);

  const bumpConversation = useCallback((key, patch, unreadDelta = 0) => {
    setConversations((prev) => prev.map((c) => (c.key === key
      ? { ...c, ...patch, unread_count: Math.max(0, (c.unread_count || 0) + unreadDelta) }
      : c)));
  }, []);

  const handleMessage = useCallback((msg, fromName, teamName) => {
    const key = conversationKeyFor(msg, user.id);
    const mine = msg.from_user_id === user.id;
    const isActive = activeRef.current === key;
    if (!conversationsRef.current.some((c) => c.key === key)) refreshConversations(); // first message from a new contact (e.g. Vibely AI)
    bumpConversation(key, {
      last_message: previewText(msg),
      last_message_type: msg.message_type,
      last_message_from: msg.team_id ? fromName : null,
      last_message_at: msg.created_at,
    }, !mine && !isActive ? 1 : 0);
    const { soundsOn, mutedChannels, pushEnabled, readAloud, ttsVoice } = settingsRef.current;
    const muted = Boolean(msg.team_id && mutedChannels.includes(msg.team_id));
    const visible = document.visibilityState === 'visible';
    // Opt-in walkie-talkie mode: speak incoming texts (even in the open conversation) while the tab is visible.
    if (!mine && readAloud && soundsOn && visible && !muted && msg.message_type === 'text' && msg.message && !isQuietNowRef.current()) {
      const prefix = msg.team_id ? `${fromName} in ${teamName}` : fromName;
      speak(`${prefix || 'Message'}: ${msg.message}`, { id: `msg-${msg.id}`, voice: ttsVoice, enqueue: true }).catch(() => {});
    }
    if (mine || isActive) return;
    if (muted) return;

    if (!visible && pushEnabled) return; // the server sends a system notification to this device
    const isVoice = msg.message_type === 'voice' && Boolean(msg.file_info?.file_id);
    const title = msg.team_id ? `${fromName} · #${teamName}` : fromName || 'New message';
    const body = previewText(msg);
    const open = () => openConversation(key);

    if (visible) toast(title, { description: body, action: { label: 'Open', onClick: open } });
    if (isQuietNowRef.current()) return; // quiet hours: visual only, no alerts or sounds
    showMessageNotification({ messageId: msg.id, title, body, onOpen: open });

    if (isVoice && visible) player.enqueue(msg); // chirp + auto-play (respects sounds toggle)
    else if (soundsOn) (isVoice ? playIncomingChirp() : playChime());
  }, [user.id, bumpConversation, openConversation, player, refreshConversations]);

  const handleTranscript = useCallback((event) => {
    const key = conversationKeyFor(event, user.id);
    setConversations((prev) => prev.map((c) => (c.key === key && c.last_message_at === event.created_at && event.transcript
      ? { ...c, last_message: event.transcript }
      : c)));
    updateNotificationTranscript(event.message_id, event.transcript);
  }, [user.id]);

  // Someone reacted to MY message while I'm in the app but not looking at that conversation → quiet toast only.
  const handleReaction = useCallback((event) => {
    if (event.action !== 'added' || event.user_id === user.id || event.from_user_id !== user.id) return;
    if (document.visibilityState !== 'visible') return;
    const key = conversationKeyFor(event, user.id);
    if (activeRef.current === key) return;
    toast(`${event.user_name} reacted ${event.emoji} to your message`, {
      description: event.message_preview ? `“${event.message_preview}”` : undefined,
      action: { label: 'Open', onClick: () => openConversation(key) },
    });
  }, [user.id, openConversation]);

  const sendRef = useRef(() => {});
  const reportActive = useCallback(() => {
    sendRef.current({ type: 'active', key: activeRef.current, visible: document.visibilityState === 'visible' });
  }, []);

  const onSocketEvent = useCallback((event) => {
    switch (event.type) {
      case '__connected': refreshConversations(); reportActive(); break;
      case 'online_users': setOnlineIds(new Set(event.users)); break;
      case 'presence':
        setOnlineIds((prev) => {
          const next = new Set(prev);
          if (event.status === 'online') next.add(event.user_id); else next.delete(event.user_id);
          return next;
        });
        break;
      case 'direct_message': handleMessage(event.message, event.from_user_name); break;
      case 'channel_message': handleMessage(event.message, event.from_user_name, event.team_name); break;
      case 'transcript': handleTranscript(event); break;
      case 'reaction': handleReaction(event); break;
      default: break;
    }
    listenersRef.current.forEach((fn) => fn(event));
  }, [refreshConversations, handleMessage, handleTranscript, handleReaction, reportActive]);

  const { status, send } = useCommsSocket(user?.id, onSocketEvent);
  sendRef.current = send;

  useEffect(() => {
    document.addEventListener('visibilitychange', reportActive);
    return () => document.removeEventListener('visibilitychange', reportActive);
  }, [reportActive]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const setActive = useCallback((key) => {
    activeRef.current = key;
    reportActive();
  }, [reportActive]);

  const enriched = useMemo(() => conversations.map((c) => (c.kind === 'team'
    ? {
      ...c,
      muted: settings.mutedChannels.includes(c.id),
      online_count: c.members.filter((m) => m.id === user.id || onlineIds.has(m.id)).length,
    }
    : { ...c, online: onlineIds.has(c.id) })), [conversations, onlineIds, settings.mutedChannels, user.id]);

  const totalUnread = useMemo(() => enriched.reduce((sum, c) => sum + (c.unread_count || 0), 0), [enriched]);

  // Installed-app icon badge (Badging API); regular tabs ignore this.
  useEffect(() => {
    if (loading || !('setAppBadge' in navigator)) return;
    (totalUnread > 0 ? navigator.setAppBadge(totalUnread) : navigator.clearAppBadge()).catch(() => {});
  }, [totalUnread, loading]);

  const value = useMemo(() => ({
    status,
    conversations: enriched,
    loading,
    totalUnread,
    refreshConversations,
    markRead,
    setActive,
    subscribe,
    openConversation,
    playingId: player.playingId,
    soundBlocked: player.soundBlocked,
    enableSound: player.enableSound,
    quietHours: quiet.quietHours,
    quietActive: quiet.quietActive,
    saveQuietHours: quiet.saveQuietHours,
    ...settings,
  }), [status, enriched, loading, totalUnread, refreshConversations, markRead, setActive, subscribe, openConversation, player.playingId, player.soundBlocked, player.enableSound, quiet.quietHours, quiet.quietActive, quiet.saveQuietHours, settings]);

  return <CommsContext.Provider value={value}>{children}</CommsContext.Provider>;
}
